import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { createIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FieldMappingEditor, type FieldMappingRow } from "./FieldMappingEditor";
import { FormField } from "./FormField";
import { mondayFormSchema, type MondayForm } from "./integrationFormSchemas";

const mappingRows = (mappings: Record<string, string> = {}): FieldMappingRow[] =>
  Object.entries(mappings)
    .filter(([key]) => key.startsWith("field."))
    .map(([key, columnId]) => ({ id: crypto.randomUUID(), field: key.slice(6), columnId }));

export function MondayConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const formSchema = mondayFormSchema.superRefine((values, ctx) => {
    if (!connection && values.apiToken.trim().length < 16) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["apiToken"], message: "API token must contain at least 16 characters." });
    }
  });
  const { register, control, handleSubmit, formState: { errors } } = useForm<MondayForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: connection?.name ?? "monday.com",
      apiToken: "",
      boardId: connection?.configSummary.boardId ?? "",
      groupId: connection?.configSummary.groupId ?? "",
      createStatusLabel: connection?.mappings?.["create.statusLabel"] ?? "",
      mappingRows: mappingRows(connection?.mappings),
    },
  });

  const submit = handleSubmit((values) => action.run("save", async () => {
    const mappings = Object.fromEntries(
      values.mappingRows.filter((row) => row.columnId.trim()).map((row) => [`field.${row.field}`, row.columnId.trim()]),
    );
    if (values.createStatusLabel) mappings["create.statusLabel"] = values.createStatusLabel;
    const config = {
      boardId: values.boardId,
      ...(values.groupId ? { groupId: values.groupId } : {}),
      ...(values.apiToken ? { apiToken: values.apiToken } : {}),
    };
    try {
      if (connection) await updateIntegrationConnection(connection.id, { name: values.name, config, mappings });
      else await createIntegrationConnection({ provider: "monday", name: values.name, config: { ...config, apiToken: values.apiToken }, mappings });
      toast.success(connection ? "monday.com settings updated." : "monday.com connected.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect monday.com.");
    }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Use a dedicated monday.com integration user where possible. IO Spoke encrypts the token and never returns it.
      </div>
      <FormField id="monday-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField id="monday-board" label="Board ID" registration={register("boardId")} error={errors.boardId} />
      <FormField id="monday-group" label="Group ID (optional)" registration={register("groupId")} error={errors.groupId} />
      <FormField id="monday-status" label="Create status label (optional)" registration={register("createStatusLabel")} error={errors.createStatusLabel} />
      <Controller
        name="mappingRows"
        control={control}
        render={({ field }) => <FieldMappingEditor rows={field.value} onChange={field.onChange} />}
      />
      <FormField
        id="monday-token"
        label={`API token ${connection ? "(leave blank to keep current)" : ""}`}
        registration={register("apiToken")}
        error={errors.apiToken}
        type="password"
        autoComplete="new-password"
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting} loading={action.saving} loadingLabel={"Saving…"}>
          {connection ? "Save settings" : "Connect monday.com"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
